import {
  extractAxiosErrorMessage,
  useOutreachGmail,
} from "@inrange/building-manager-api-client";
import { useContext, useEffect, useRef, useState } from "react";
import { Alert, Button, Spinner } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserContext } from "../../auth/UserContext";
import {
  clearOutreachGmailCallbackParams,
  OUTREACH_GMAIL_CALLBACK_PATH,
  peekOutreachGmailCallbackParams,
} from "./outreachGmailCallbackUrl";

// Connecting the account that outreach drafts land in, and the OAuth callback that
// completes it. Both live here because they are two halves of one flow, and splitting
// them would put the redirect URI in one file and the code that has to match it in
// another.
const callbackUri = () =>
  `${window.location.origin}${OUTREACH_GMAIL_CALLBACK_PATH}`;

// Rendered wherever the operator meets the fact that no mailbox is connected. Sends
// them to Google's consent screen; the callback route below finishes the job.
export const OutreachGmailConnectButton: React.FC<{
  loginHint?: string | null;
  label?: string;
}> = ({ loginHint, label = "Connect the sending account" }) => {
  const { connectGmail } = useOutreachGmail();
  const [error, setError] = useState<string | null>(null);

  const start = () => {
    setError(null);
    connectGmail.mutate(
      { redirectUri: callbackUri(), loginHint: loginHint ?? null },
      {
        // A full navigation, not a popup: Kinde's provider closes any same-origin tab
        // that carries a `window.opener`, which is exactly what a popup would be (see
        // frontends CLAUDE.md on `window.open` + noopener).
        onSuccess: (data) => window.location.assign(data.authUrl),
        onError: async (err) => setError(await extractAxiosErrorMessage(err)),
      }
    );
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline-primary"
        onClick={start}
        disabled={connectGmail.isLoading}
      >
        {connectGmail.isLoading ? "Opening Google..." : label}
      </Button>
      {error && (
        <div className="text-danger" style={{ fontSize: 12, marginTop: 4 }}>
          {error}
        </div>
      )}
    </>
  );
};

const googleErrorMessage = (googleError: string) =>
  googleError === "access_denied"
    ? "Google sign-in was cancelled, so nothing was connected."
    : `Google returned an error: ${googleError}`;

// Where Google sends the operator back to. Exchanges the code for a refresh token
// server-side, then hands them back to the leads page.
//
// The code and state never reach this component through the URL: index.tsx stashes
// them before anything reads the address bar (see outreachGmailCallbackUrl), and the
// stash is removed just before the exchange. So a reload or a browser Back afterwards
// lands here with nothing to exchange, rather than re-submitting a spent code.
export const OutreachGmailCallbackRoute: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { completeGmailConnect } = useOutreachGmail();
  // `googleError`, not `error`: the lift re-keys it, because Kinde v5's provider
  // aborts its init on any `?error=` param whatever the path.
  const googleError = params.get("googleError");
  const [callback] = useState(peekOutreachGmailCallbackParams);
  const [error, setError] = useState<string | null>(
    googleError ? googleErrorMessage(googleError) : null
  );
  const [done, setDone] = useState<string | null>(null);
  // An authorisation code is single-use: exchanging it twice fails the second time,
  // so the ref makes this run once per mount whatever re-renders happen around it.
  const exchanged = useRef(false);

  useEffect(() => {
    if (exchanged.current || googleError || !callback) return;
    exchanged.current = true;
    clearOutreachGmailCallbackParams();
    completeGmailConnect.mutate(
      {
        code: callback.code,
        state: callback.state,
        redirectUri: callbackUri(),
        userEmail: user?.email ?? null,
      },
      {
        onSuccess: (data) => setDone(data.googleEmail),
        onError: async (err) => setError(await extractAxiosErrorMessage(err)),
      }
    );
    // Deliberately once-per-mount: re-running on a mutation-identity change would
    // re-submit a code Google has already spent.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Replace, not push: the callback entry is spent, so Back from the leads page
  // should not land on it again.
  const backToLeads = (
    <Button
      size="sm"
      variant="primary"
      onClick={() => navigate("/leads", { replace: true })}
    >
      Back to leads
    </Button>
  );

  if (!callback && !googleError) {
    return (
      <div style={{ padding: 24, maxWidth: 640 }}>
        <h5>Connecting Gmail for outreach drafts</h5>
        <p>
          Nothing to finish here. If you were connecting Gmail, check the
          sending account in Data &amp; Config and connect again if it
          isn&rsquo;t shown.
        </p>
        {backToLeads}
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 640 }}>
      <h5>Connecting Gmail for outreach drafts</h5>
      {!done && !error && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Spinner animation="border" size="sm" />
          <span>Finishing the connection...</span>
        </div>
      )}
      {done && (
        <Alert variant="success">
          Outreach drafts will now be written into <strong>{done}</strong>.
        </Alert>
      )}
      {error && <Alert variant="danger">{error}</Alert>}
      {(done || error) && backToLeads}
    </div>
  );
};
